"use client"
import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap"; 

import { formatDurations } from '../../lib/function';
import { FaArrowRight } from "react-icons/fa"; // FontAwesome Icon

interface BillingOption {
  value: number;
  label: string;
}

interface Plan {
  id: string,
  name: string;
  price: string;
  billingPeriod: string;
  configureUrl: string;
  features: string[];
  billingOptions: BillingOption[];
}

interface SlugData{
    rawSlug : string;
    id: string
}

const PlanCard: React.FC<{ plan: Plan }> = ({ plan }) => {
  const [selectedBilling, setSelectedBilling] = useState(plan.billingOptions[0].value);

  const handleBillingChange = (value: string) => {
    setSelectedBilling(value);
  };

  return (
    <div className="my-2"> 
      <div className="card-body text-center">
        <h5 className="textHad fw-bold display-6 fs-5">{plan.name}</h5>
        <ul className="list-unstyled">
          {plan.features.map((feature, index) => (
            <li className="textPara fs-6 fw-normal lh-lg" key={index}>✔ {feature}</li>
          ))}
        </ul>
        <div>
          <label htmlFor={`billing-options-${plan.id}`} className="form-label">
            Billing Options:
          </label>
          <select
            id={`billing-options-${plan.id}`}
            className="form-select"
            value={selectedBilling}
            onChange={(e) => handleBillingChange(e.target.value)}
          >
            {plan.billingOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {/* <div className="d-flex justify-content-between align-items-center mt-3">  */}
        <div className="text-center mt-3 mb-2"> 
          <span className="fw-bold display-6 textOrange m-0 p-0">{plan.price}</span> 
          <p className="textPara fs-6 mb-2 p-0">/month</p>  
          <Button  href={plan.configureUrl} className="orangeBtn fw-semibold" size="lg">
          <span> Configure </span> <FaArrowRight className="" />
                </Button> 
        </div>
      </div>
    </div>
  );
};

const ProxyServer: React.FC<{rawSlug : SlugData, id: SlugData}> = ({rawSlug, id}) => {
  // Extract the slug from the pathname
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Fetch the pricing plans from the API
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        const response = await fetch('/api/plan', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rawSlug
          }),
        });
        
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();
        
        const ids = id.split(',').map(i => i.trim()).filter(i => i !== '');
        const slicedData = result.data.slice(0, 100);

        const filteredData = ids.length > 0
          ? slicedData.filter(post => ids.includes(post.id.toString()))
          : slicedData;

        const mappedData: Plan[] = filteredData.map((post) => ({
          id: post.id.toString(),
          name: post.name,
          price: formatDurations(post.duration)[0]['value'],
          billingPeriod: `$${Math.floor(Math.random() * 100)} / month (1 year)`,
          features: post.description.split(/\r?\n/)
            .filter(line => line.includes(':'))
            .map(line => {
              const [key, ...rest] = line.split(':');
              return `${key.trim()} - ${rest.join(':').trim()}`;
            }),
          configureUrl: `${process.env.NEXT_PUBLIC_API_BASE_URL}/cart.php?a=add&pid=${post.id}&billingcycle=${post.duration?.monthly?.type ?? post.duration?.annually?.type}`,
          billingOptions: formatDurations(post.duration)
        }));

        setPlans(mappedData);
      } catch (error) {
        setError("Error fetching pricing plans");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchPricingPlans();
  }, [rawSlug, id]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="">
    <div className="container">  
      <div className="row">
        {plans.map((plan) => (
          <div className="col-xl-3 col-lg-4 col-md-6 col-12" key={plan.id}>
            <PlanCard plan={plan} />
          </div>
        ))}
      </div>
    </div>
    </div>
  );
};

export default ProxyServer;
